/**
 * MenuBar.tsx — the desktop-style top menu bar.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ File  Settings  About    ▶ ⏸ ↺      ● status · step N      │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Left: standard desktop menus (File / Settings / About). The View menu is
 * gone — every view toggle moved to the bottom StatusBar.
 * Center: the transport cluster (Play/Pause + Reset). Centered so the sim's
 * primary affordance is visually prominent.
 * Right: a colored status dot, a step counter, and (when an API source is
 * wired) a connection dot.
 *
 * File menu order (was a flat dump of download/export entries — now clean):
 *   Library · Open… · Save · Save As…
 *   ───
 *   Download… ▸   (opens a format picker: Project ZIP / Bundle JSON)
 *   Export…   ▸   (opens a format picker: CSV wide / CSV long / Trajectory)
 *
 * Menu behavior: click a label to open, hover a sibling to switch (only when
 * one is already open), click an entry to act + close, click outside / Esc to
 * dismiss.
 */
import { useEffect, useRef, useState } from 'react';
import {
  ArrowClockwiseIcon as ArrowClockwise,
  ArrowCounterClockwiseIcon as ArrowCounterClockwise,
  ChartBarIcon as ChartBar,
  GraphIcon as Graph,
  CodeIcon as Code,
  ChatTeardropDotsIcon as ChatTeardropDots,
  ClockCounterClockwiseIcon as ClockCounterClockwise,
  Cube,
  DatabaseIcon as Database,
  DotsThreeVerticalIcon as DotsThreeVertical,
  DownloadSimpleIcon as DownloadSimple,
  FloppyDiskIcon as FloppyDisk,
  FolderOpenIcon as FolderOpen,
  FlowArrowIcon as FlowArrow,
  GearIcon as Gear,
  ListIcon as List,
  StopIcon as Stop,
  PlayIcon as Play,
  RecordIcon as Record,
  RobotIcon as Robot,
  PlusIcon as Plus,
  PencilSimpleIcon as PencilSimple,
  QuestionIcon as Question,  SidebarSimpleIcon as SidebarSimple,
  SpinnerGapIcon as SpinnerGap,
  TrashIcon as Trash,
  UploadSimpleIcon as UploadSimple,
  XIcon as X,

  SunIcon as Sun,
  MoonIcon as Moon,
} from '@phosphor-icons/react';
import { FileTabs, type OpenFile } from '../code/FileTabs';
import { GraphTabs } from '../graph/GraphTabs';
import { WorkflowTabs } from '../workflow/WorkflowTabs';

export type Theme = 'dark' | 'light';

/** A single line in a dropdown. Entries act on click; separators are visual. */
type MenuItem =
  | { type: 'separator' } & any
  | {
      type: string;
      label: string;
      checked?: boolean;
      disabled?: boolean;
      icon?: typeof Play;
      /** Small muted hint rendered to the right of the label (e.g. the
       *  connected API URL). */
      meta?: string;
      onSelect: () => void;
      className?: string
    };

interface MenuDef {
  id: string;
  label: string;
  items: MenuItem[];
}

interface MenuBarProps {
  // File
  canOpen: boolean;
  canSave: boolean;
  onSave: () => void;
  onSaveAs: () => void;
  onLibrary: () => void;
  onOpen: (file: File) => void;
  onClose: () => void;
  /** The hidden file input behind Open… — owned by the App so the Ctrl+O
   *  binding can click the same input. When absent, MenuBar renders its own. */
  openInputRef?: React.RefObject<HTMLInputElement | null>;
  // Edit history (graph undo/redo — mirrors Ctrl+Z / Ctrl+Shift+Z)
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  /** Open the chat panel (backend-gated LLM surface; only shown when canRemote). */
  onOpenChat?: () => void;
  /** Open the one-shot Ask box (backend-gated; the visible Cmd/Ctrl+K entry
   *  point — inserts at the cursor in the focused editor). */
  onOpenAsk?: () => void;
  /** Open the Services modal (running docker containers). */
  onOpenServices?: () => void;
  /** The automatic version checker found a drift (frontend or stack). When
   *  true AND onOpenUpdate is set, an "Update available…" entry appears in
   *  the hamburger menu (the Update modal). */
  updateAvailable?: boolean | null;
  /** Open the Update modal (the in-app updater). */
  onOpenUpdate?: () => void;
  /** Open the local Docker-services management panel (local-mode only).
   *  When present, a "Local Services…" entry appears in the hamburger menu. */
  /** Open the Download format picker (Project ZIP / Bundle JSON). */
  onDownloadPicker: () => void;
  /** Whether the Project ZIP download option is available. */
  canDownloadProject?: boolean;
  /** Open the Export format picker (CSV wide / long / trajectory). */
  onExportPicker: () => void;
  /** Whether export-run is available (an API source is wired). */
  canExportRun?: boolean;
  // Settings
  canReset: boolean;
  onReset: () => void;
  /** Open the consolidated Settings modal. Settings + tick rate + connection
   *  now live there (one tabbed surface) rather than scattered across a
   *  Settings dropdown. The Menu still has a "Settings…" entry that opens it. */
  onOpenSettings: () => void;
  // About
  onHelp: () => void;
  version: string;
  // Transport / status
  status: 'idle' | 'running' | 'stopped';
  running: boolean;
  onStart: () => void;
  onStop: () => void;
  /** The stop flow is in flight (stop model → poll tasks → "Stop other
   *  running tasks?" modal → follow-up stops). While true the transport
   *  renders a disabled spinner in place of the Stop/Play button — the model
   *  may already report stopped while the modal is still waiting for an
   *  answer, and flipping to Play mid-flow would be misleading. */
  stopping?: boolean;
  /** Live-recording state (the Record button). ``undefined`` when no backend
   *  recording surface is wired (the button is disabled, not hidden — the
   *  transport reads as one cluster). Arming records the ring buffer AND the
   *  durable dataset run (per the manifest's record.saveDataset, default
   *  true — docs/TRAINING.md §4). */
  recording?: boolean;
  /** Toggle the recording (arm ↔ disarm). */
  onToggleRecord?: () => void;
  /** The run-level agents kill-switch state (from the polled run status).
   *  ``undefined`` hides the toggle (no backend project / no agents
   *  configured / an older backend without the route). */
  agentsEnabled?: boolean;
  /** Toggle the agents kill-switch on/off. When off, the relay path skips
   *  agent application — ticks pass through unmodified. */
  onToggleAgents?: () => void;
  /** Relay mode (Phase D): an external model POSTs ticks; the backend's demo
   *  pump is off. When true, the Play/Pause transport is hidden and a "relay"
   *  tag takes its place. The StatusBar owns the live "Listening" affordance. */
  relay?: boolean;
  /** Hide the transport cluster (Play/Reset). Set when the Code tab is active —
   *  the Code surface owns its own run controls (the Tasks panel); the global
   *  transport is noise there. The status dots on the right are always hidden
   *  now (redundant with the StatusBar). */
  hideTransport?: boolean;
  // ── Code tab (View | Code shell switch). The tabs live inside this bar so
  //    they reuse its height + flex layout — no extra row. Only rendered when
  //    `showViewTabs` is true (a backend project is open: canRemote). The Code
  //    tab is a single surface — a notebook is just another file (.ipynb) that
  //    opens in a tab, so there is no Files|Notebook sub-switch. ──
  /** Whether to show the View/Data/Plot/Code/Workflows tabs at all (false in local mode). */
  showViewTabs?: boolean;
  /** The active shell mode. */
  view?: 'view' | 'data' | 'plot' | 'code' | 'workflows';
  onView?: (v: 'view' | 'data' | 'plot' | 'code' | 'workflows') => void;
  // ── Inline file tabs. When in Code view, the open-file tabs render inside
  //    the MenuBar (after the View/Code switcher) instead of a separate row —
  //    densifies the UI (one less panel). Driven through the CodePanel
  //    imperative handle. ──
  codeTabs?: OpenFile[];
  codeActiveTab?: string | null;
  onSelectCodeTab?: (path: string) => void;
  onCloseCodeTab?: (path: string) => void;
  onSaveCodeTab?: (path: string) => void;
  onRenameCodeTab?: (path: string) => void;
  onCreateCodeEntry?: (sql?: boolean) => void;
  // ── Code-tab Save button. Lives in the MenuBar (after the Code tab) so it's
  //    always reachable while editing; only rendered when Code is the active
  //    shell AND there are dirty files. The count is the number of unsaved
  //    text-file tabs; 0 hides the button entirely. ──
  codeDirtyCount?: number;
  onCodeSave?: () => void;
  // ── Plot-tab dashboard tabs + rail toggle. When in Plot view, the dashboard
  //    tabs render inline (after the View switcher) like code file-tabs. The
  //    rail toggle shows/hides the plot-registry left panel. ──
  plotDashboards?: { id: string; name: string }[];
  plotActiveDashboard?: string | null;
  onSelectDashboard?: (id: string) => void;
  onCreateDashboard?: () => void;
  onRenameDashboard?: (id: string) => void;
  onDeleteDashboard?: (id: string) => void;
  // ── Left Sidebar Toggle ──
  showLeftSidebar?: boolean;
  onToggleLeftSidebar?: () => void;
  // ── Graph tabs (multi-graph) — pinned, always visible in View mode ──
  graphs?: { id: string; name: string }[];
  activeGraphId?: string;
  onSelectGraph?: (id: string) => void;
  onCreateGraph?: () => void;
  onRenameGraph?: (id: string) => void;
  onDeleteGraph?: (id: string) => void;
  // ── Replay tabs (View mode) — transient read-only recorded periods with
  //    an inline close-X, rendered after the graph tabs; the trailing clock
  //    button opens the ReplayPicker. ──
  replayTabs?: { key: string; label: string; runId?: string }[];
  activeReplayKey?: string | null;
  onSelectReplayTab?: (key: string) => void;
  onCloseReplayTab?: (key: string) => void;
  /** Double-click a replay tab → rename (the run's display name for
   *  run-derived tabs, the doc's label for saved periods). */
  onRenameReplayTab?: (key: string) => void;
  onOpenReplayPicker?: () => void;
  // ── Workflows-tab tabs. Open documents (closable), picked through the
  //    picker modal (the `+`); driven through the WorkflowPanel handle. ──
  workflowTabs?: { id: string; name: string; error?: boolean }[];
  workflowActiveTab?: string | null;
  onSelectWorkflowTab?: (id: string) => void;
  onCloseWorkflowTab?: (id: string) => void;
  onAddWorkflowTab?: () => void;
  theme?: 'dark' | 'light';
  onToggleTheme?: () => void;
}

const HZ_CHOICES = [1, 2, 4, 8, 16];

/** Keyboard-hint text for menu entries ("⌘Z" on Mac, "Ctrl+Z" elsewhere).
 *  `combo` is the non-modifier part ("Z", "Shift+Z", …). */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform);
function kbdHint(combo: string): string {
  return IS_MAC ? `⌘${combo}` : `Ctrl+${combo}`;
}

export function MenuBar(props: MenuBarProps): JSX.Element {
  const { onOpen } = props;
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const barRef = useRef<HTMLElement | null>(null);
  // Prefer the App-owned input (shared with the Ctrl+O binding); fall back to
  // a local one when the App doesn't pass a ref.
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = props.openInputRef ?? localInputRef;

  // Click-outside closes the open menu. mousedown so it fires before the
  // entry's own onClick resolves — but entry clicks stopPropagation so this
  // only catches genuine outside clicks.
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  const runEntry = (fn?: () => void) => () => {
    fn?.();
    setOpenMenu(null);
  };

  // Build a single "Menu" dropdown. The old File/Settings/About split is gone —
  // the bar is one icon menu (a hamburger `List`) that nests file ops, a
  // "Settings…" entry (opens the consolidated Settings modal), and the About
  // section. Settings/tick-rate/connection no longer live in a dropdown — they
  // live in the Settings modal (`SettingsModal`). `menus` is recreated each
  // render (cheap), so handlers always see fresh props.
  const menus: MenuDef[] = [
    {
      id: 'menu',
      // The label is rendered as an icon (List); `renderLabel` picks it up.
      label: 'Menu',
      items: [
        // ── Edit history (graph undo/redo; mirrors Ctrl+Z / Ctrl+Shift+Z) ──
        { type: 'block-start', label: 'Undo', icon: ArrowCounterClockwise, meta: kbdHint('Z'),
          disabled: !props.canUndo, onSelect: runEntry(props.onUndo), className: "menu-item-50" },
        { type: 'block-end', label: 'Redo', icon: ArrowClockwise, meta: kbdHint('Shift+Z'),
          disabled: !props.canRedo, onSelect: runEntry(props.onRedo), className: "menu-item-50" },
        { type: 'separator' },
        // ── File ops ──
        { type: 'entry', label: 'Library…', icon: FolderOpen, disabled: !props.canOpen,
          meta: kbdHint('Shift+P'),
          onSelect: runEntry(props.onLibrary) },
        { type: 'entry', label: 'Open…', icon: FolderOpen, disabled: !props.canOpen,
          meta: kbdHint('O'),
          onSelect: runEntry(() => fileInputRef.current?.click()) },
        { type: 'entry', label: 'Save', icon: FloppyDisk, disabled: !props.canSave,
          meta: kbdHint('S'),
          onSelect: runEntry(props.onSave) },
        { type: 'entry', label: 'Save As…', icon: FloppyDisk, disabled: !props.canSave,
          meta: kbdHint('Shift+S'),
          onSelect: runEntry(props.onSaveAs) },
        { type: 'separator' },
        // Download opens a format picker so the user picks what they want
        // (Project ZIP vs Bundle JSON) rather than a flat list of entries.
        { type: 'entry', label: 'Download…', icon: DownloadSimple,
          onSelect: runEntry(props.onDownloadPicker) },
        // Export opens a format picker for the training-data exports
        // (CSV wide/long, trajectory JSONL). Gated on an API source.
        { type: 'entry', label: 'Export…', icon: UploadSimple,
          disabled: !props.canExportRun,
          onSelect: runEntry(props.onExportPicker) },
        // ── Settings ──
        { type: 'separator' },
        { type: 'entry', label: 'Settings…', icon: Gear,
          onSelect: runEntry(props.onOpenSettings) },
        // The automatic version checker's entry point (src/version/): shown
        // only when a drift was detected.
        ...(props.onOpenUpdate && props.updateAvailable ? [{
          type: 'entry', label: 'Update available…', icon: ArrowClockwise,
          meta: 'new version', onSelect: runEntry(props.onOpenUpdate),
        }] : []),
        // Reset is redundant here — it lives in the transport cluster (top
        // right) and the Settings modal. Commented out to avoid the duplicate.
        // { type: 'entry', label: 'Reset', icon: ArrowCounterClockwise,
        //   disabled: !props.canReset, onSelect: runEntry(props.onReset) },
        // ── About ──
        { type: 'separator' },
        { type: 'entry', label: 'About', icon: Question, onSelect: runEntry(props.onHelp) },
        { type: 'entry', label: props.theme === 'dark' ? 'Light mode' : 'Dark mode', icon: props.theme === 'dark' ? Sun : Moon, onSelect: runEntry(props.onToggleTheme) },

        { type: 'separator' },
        { type: 'entry', label: 'Close', icon: X, onSelect: runEntry(props.onClose) },
      ],
    },
  ];

  return (
    <>
      {openMenu && (
        <div
          className="context-overlay"
          style={{ zIndex: 90 }} // Below the menu bar (z-index: 100) but above everything else
          onClick={() => setOpenMenu(null)}
          onContextMenu={(e) => {
            e.preventDefault();
            setOpenMenu(null);
          }}
        />
      )}
      <header className="menu-bar" ref={barRef}>
        <nav className="menu-bar-nav">
        {menus.map((m) => (
          <Menu
            key={m.id}
            menu={m}
            open={openMenu === m.id}
            anyOpen={openMenu !== null}
            onLabelClick={() => setOpenMenu((cur) => (cur === m.id ? null : m.id))}
            onLabelEnter={() => {
              // Hover-to-switch only when some menu is already open.
              if (openMenu !== null && openMenu !== m.id) setOpenMenu(m.id);
            }}
          />
        ))}
        {/* View | Code tabs — inline in the menu bar so they reuse its 32px
            height (no extra panel row). Only shown for backend projects. The
            Code tab is one surface (no Files|Notebook sub-switch): a notebook
            is a file that opens in a tab like any other. */}
        {props.showViewTabs && (
          <div className="view-tabs" role="tablist" aria-label="View mode">
            {/* Left sidebar toggle */}
            {props.onToggleLeftSidebar && (
              <button
                type="button"
                className={`view-tab view-tab-icon${props.showLeftSidebar ? ' active' : ''}`}
                onClick={props.onToggleLeftSidebar}
                title="Toggle sidebar"
                aria-label="Toggle sidebar"
              >
                <SidebarSimple size={14} aria-hidden />
              </button>
            )}
            {/* Ask — the one-shot query box (⌘K). Sits LEFT of Chat: the
                quick "insert at cursor" action before the full conversation.
                Icon-only like Chat, sharing the view-tab row's chrome. */}
            {/* {props.onOpenAsk && (
              <button
                type="button"
                className="view-tab view-tab-icon"
                onClick={props.onOpenAsk}
                title="Ask (⌘K)"
                aria-label="Open the one-shot ask box"
              >
                <Lightning size={14} aria-hidden />
              </button>
            )} */}
            {/* Chat — backend-gated LLM surface. Icon-only action button that
                opens the chat modal; lives with the view tabs so it shares
                their divider + height. Not a view selector (opens a modal). */}
            {props.onOpenChat && (
              <button
                type="button"
                className="view-tab view-tab-icon"
                onClick={props.onOpenChat}
                title="Chat"
                aria-label="Open chat"
              >
                <ChatTeardropDots size={14} aria-hidden />
              </button>
            )}
            <button
              type="button"
              role="tab"
              aria-selected={props.view === 'view'}
              className={`view-tab${props.view === 'view' ? ' active' : ''}`}
              onClick={() => props.onView?.('view')}
            >
              <Graph size={14} aria-hidden />
              Graph
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={props.view === 'workflows'}
              className={`view-tab${props.view === 'workflows' ? ' active' : ''}`}
              onClick={() => props.onView?.('workflows')}
              title="Durable workflows - code-derived graphs + run control"
            >
              <FlowArrow size={14} aria-hidden />
              Workflows
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={props.view === 'plot'}
              className={`view-tab${props.view === 'plot' ? ' active' : ''}`}
              onClick={() => props.onView?.('plot')}
              title="Dashboard builder — drop plots from the registry onto a canvas"
            >
              <ChartBar size={14} aria-hidden />
              Analytics
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={props.view === 'data'}
              className={`view-tab${props.view === 'data' ? ' active' : ''}`}
              onClick={() => props.onView?.('data')}
              title="Project data — records grid + SQL console"
            >
              <Database size={14} aria-hidden />
              Data
            </button>
            
            <button
              type="button"
              role="tab"
              aria-selected={props.view === 'code'}
              className={`view-tab${props.view === 'code' ? ' active' : ''}`}
              onClick={() => props.onView?.('code')}
            >
              <Code size={14} aria-hidden />
              Code
            </button>
            
          </div>
        )}
        {/* Save — only while the Code tab is active AND there are dirty files.
            Renders as a compact "Save N" pill with a count badge so it's
            glanceable but doesn't take a permanent slot. */}
        {props.showViewTabs && props.view === 'code' && (props.codeDirtyCount ?? 0) > 0 && (
          <button
            type="button"
            className="code-save-pill"
            onClick={props.onCodeSave}
            title={`Save ${props.codeDirtyCount} unsaved file${props.codeDirtyCount === 1 ? '' : 's'}`}
          >
            <FloppyDisk size={13} aria-hidden />
            Save
            <span className="code-save-count">{props.codeDirtyCount}</span>
          </button>
        )}
        {/* Open-file tabs — inline in the MenuBar when in Code view, after the
            View/Code switcher + Save pill. Replaces the separate tab row that
            used to sit above the editor (densifies the layout by one row). */}
        {props.view === 'code' && props.codeTabs && props.codeTabs.length > 0 && (
          <>
            <div className="view-tab-divider" aria-hidden />
            <FileTabs
            tabs={props.codeTabs}
            activePath={props.codeActiveTab ?? null}
            onSelect={(p) => props.onSelectCodeTab?.(p)}
            onClose={(p) => props.onCloseCodeTab?.(p)}
            onSave={(p) => props.onSaveCodeTab?.(p)}
            onRename={(p) => props.onRenameCodeTab?.(p)}
            onCreateEntry={(sql) => props.onCreateCodeEntry?.(sql)}
          />
          </>
        )}
        {/* Graph tabs (multi-graph) — pinned strip, always visible in View
            mode (like dashboard tabs in Plot mode). No close-X on any tab;
            rename via pencil → PromptDialog; delete non-Main via trash →
            ConfirmDialog; Main is non-removable. Switching is per-client. */}
        {props.view === 'view' && props.graphs && props.graphs.length > 0 && (
          <>
            <div className="view-tab-divider" aria-hidden />
            <GraphTabs
            graphs={props.graphs}
            activeId={props.activeGraphId ?? props.graphs[0]?.id ?? 'main'}
            onSelect={(id) => props.onSelectGraph?.(id)}
            onCreate={() => props.onCreateGraph?.()}
            onRename={(id) => props.onRenameGraph?.(id)}
            onDelete={(id) => props.onDeleteGraph?.(id)}
            replays={props.replayTabs ?? []}
            activeReplayKey={props.activeReplayKey ?? null}
            onSelectReplay={(key) => props.onSelectReplayTab?.(key)}
            onCloseReplay={(key) => props.onCloseReplayTab?.(key)}
            onRenameReplay={(key) => props.onRenameReplayTab?.(key)}
            onOpenReplayPicker={() => props.onOpenReplayPicker?.()}
          />
          </>
        )}
        {/* The picker entry survives even when the project has no graph tabs
            yet (an empty-spec scaffold) — a recording is still replayable. */}
        {props.view === 'view' && (!props.graphs || props.graphs.length === 0) && (
          <>
            <div className="view-tab-divider" aria-hidden />
            <button
              type="button"
              className="graph-tab graph-tab-add"
              title="Open a replay (browse recordings)"
              onClick={() => props.onOpenReplayPicker?.()}
            >
              <ClockCounterClockwise size={14} />
            </button>
          </>
        )}
        {/* Workflows-tab tabs - open documents (closable), mirroring the
            graph/dashboard tab chrome. The `+` opens the workflow picker. */}
        {props.view === 'workflows' && props.workflowTabs && props.workflowTabs.length > 0 && (
          <>
            <div className="view-tab-divider" aria-hidden />
            <WorkflowTabs
              tabs={props.workflowTabs}
              activeId={props.workflowActiveTab ?? null}
              onSelect={(id) => props.onSelectWorkflowTab?.(id)}
              onClose={(id) => props.onCloseWorkflowTab?.(id)}
              onAdd={() => props.onAddWorkflowTab?.()}
            />
          </>
        )}
        {props.view === 'workflows' && (props.workflowTabs ?? []).length === 0 && (
          <>
            <div className="view-tab-divider" aria-hidden />
            <button
              type="button"
              className="view-tab"
              onClick={() => props.onAddWorkflowTab?.()}
              title="Open a workflow"
            >
              <Plus size={14} aria-hidden />
              Open workflow…
            </button>
          </>
        )}
        {/* Plot-tab dashboard tabs — inline like code file-tabs. The rail
            toggle (sidebar show/hide) leads, mirroring the View tab's sidebar
            toggle in the StatusBar. Only shown in Plot view. */}
        {props.view === 'plot' && (
          <>
            <div className="view-tab-divider" aria-hidden />
            <div className="dashboard-tabs dashboard-tabs-inline">
              {(props.plotDashboards ?? []).map((d) => (
                <div
                  key={d.id}
                  className={`dashboard-tab${props.plotActiveDashboard === d.id ? ' active' : ''}`}
                  onClick={() => props.onSelectDashboard?.(d.id)}
                >
                  <span className="dashboard-tab-name">{d.name}</span>
                  {props.plotActiveDashboard === d.id && (
                    <span className="dashboard-tab-actions">
                      <button
                        type="button"
                        className="btn-icon-mini"
                        title="Rename dashboard"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onRenameDashboard?.(d.id);
                        }}
                      >
                        <PencilSimple size={11} />
                      </button>
                      <button
                        type="button"
                        className="btn-icon-mini"
                        title="Delete dashboard"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onDeleteDashboard?.(d.id);
                        }}
                      >
                        <Trash size={11} />
                      </button>
                    </span>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="dashboard-tab dashboard-tab-add"
                title="New dashboard"
                onClick={() => props.onCreateDashboard?.()}
              >
                <Plus size={14} />
              </button>
            </div>
          </>
        )}
      </nav>

      {/* Transport cluster now lives on the RIGHT (the old center slot is
          empty; the old right-side status dots + step counter were redundant
          with the StatusBar and are removed). In relay mode these start/stop
          the backend model subprocess; otherwise they drive the local engine /
          WS demo pump. Hidden entirely in the Code view — the Code surface
          owns its own run controls (the Tasks panel). The Record button sits
          BEFORE Play: arming the recording is the thing you do first, then
          start the run. After Play, the ⋮ transport menu holds the emergency
          stop (agents kill-switch). */}
      <div className="menu-right">
        {!props.hideTransport && (
          <>
            <button
              type="button"
              className={`btn-icon-mini btn-transport record-btn${props.recording ? ' on' : ''}`}
              onClick={props.onToggleRecord}
              disabled={!props.onToggleRecord}
              title={
                props.recording
                  ? 'Stop recording (close the dataset run)'
                  : 'Record (arm capture + durable dataset)'
              }
              aria-label={props.recording ? 'Stop recording' : 'Record'}
              aria-pressed={props.recording || undefined}
            >
              <Record size={15} weight="fill" />
            </button>
            {props.stopping ? (
              <button
                type="button"
                className="btn-icon-mini btn-transport"
                disabled
                title="Stopping…"
                aria-label="Stopping"
              >
                <SpinnerGap className="spin" size={15} aria-hidden />
              </button>
            ) : props.running ? (
              <button
                type="button"
                className="btn-icon-mini btn-transport"
                onClick={props.onStop}
                title={props.relay ? 'Stop backend model' : 'Stop'}
                aria-label="Stop"
              >
                <Stop size={15} weight="fill" />
              </button>
            ) : (
              <button
                type="button"
                className="btn-icon-mini btn-transport"
                onClick={props.onStart}
                title={props.relay ? 'Start backend model' : 'Play'}
                aria-label="Play"
              >
                <Play size={15} weight="fill" />
              </button>
            )}
            {/* ⋮ Transport menu — the overflow home after Play. Holds the
                Services… entry (docker containers) and, when the backend
                exposes it, the agents kill-switch (EMERGENCY STOP for agent
                writes — red while disabled so the state stays glanceable
                without opening the menu). */}
            {(props.onOpenServices || (props.onToggleAgents && props.agentsEnabled !== undefined)) && (
              <div
                className="menu-slot"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={`btn-icon-mini btn-transport transport-menu-btn${props.agentsEnabled === undefined || props.agentsEnabled ? '' : ' off'}`}
                  onClick={() =>
                    setOpenMenu((cur) => (cur === 'transport' ? null : 'transport'))
                  }
                  aria-haspopup="menu"
                  aria-expanded={openMenu === 'transport'}
                  aria-label="Transport options"
                  title={
                    props.agentsEnabled
                      ? 'Transport options (services, emergency stop)'
                      : 'Agents DISABLED — ticks pass through unmodified; open to re-enable'
                  }
                >
                  <DotsThreeVertical size={15} weight="bold" aria-hidden />
                </button>
                {openMenu === 'transport' && (
                  <div className="menu-dropdown menu-dropdown-right" role="menu">
                    {props.onOpenServices && (
                      <MenuEntry
                        item={{
                          type: 'entry',
                          label: 'Services…',
                          icon: Cube,
                          meta: 'docker containers',
                          onSelect: runEntry(props.onOpenServices),
                        }}
                      />
                    )}
                    {props.onToggleAgents && props.agentsEnabled !== undefined && (
                      <MenuEntry
                        item={{
                          type: 'entry',
                          label: props.agentsEnabled
                            ? 'Emergency stop'
                            : 'Re-enable agents',
                          icon: Robot,
                          meta: props.agentsEnabled
                            ? 'disable agent writes'
                            : 'agent writes resume',
                          onSelect: runEntry(props.onToggleAgents),
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Hidden file input driven by Menu → Open (and the Ctrl+O binding via
          the App-owned ref). The cast reconciles the null-typed ref with
          React 18's input-ref typing. */}
      <input
        ref={fileInputRef as React.RefObject<HTMLInputElement>}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onOpen(f);
          e.target.value = ''; // allow re-picking the same file later
        }}
      />
    </header>
    </>
  );
}

/** One menu label + its dropdown. The "menu" id renders as a hamburger icon
 *  (List) instead of a text label — the bar leads with a menu glyph. */
function Menu({
  menu,
  open,
  anyOpen,
  onLabelClick,
  onLabelEnter,
}: {
  menu: MenuDef;
  open: boolean;
  anyOpen: boolean;
  onLabelClick: () => void;
  onLabelEnter: () => void;
}): JSX.Element {
  const isIconMenu = menu.id === 'menu';
  useEffect(() => {
    let start = document.querySelector('.bloq-wrap')
    document.querySelectorAll('.menu-dropdown .bloq').forEach((e) => {
      start?.appendChild(e)
    })
  })
  return (
    <div
      className={`menu-slot${isIconMenu ? ' icon-menu' : ''}`}
      // mousedown so the click that opens the menu doesn't also get caught by
      // the bar's outside-click handler (still bubbling when it runs). We stop
      // propagation only on this slot; entry clicks do their own.
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`menu-bar-item${open ? ' open' : ''}`}
        // Click toggles. If a different menu is open, hover has already switched.
        onClick={onLabelClick}
        onMouseEnter={onLabelEnter}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={menu.label}
        data-any-open={anyOpen ? '1' : '0'}
      >
        {isIconMenu ? <List size={16} aria-hidden /> : menu.label}
      </button>
      {open && (
        <div className="menu-dropdown" role="menu">
          {menu.items.map((it, idx) =>
            it.type === 'separator' ? (
              <div key={`sep-${idx}`} className="menu-separator" />
            ) : (
              <>
              {it.type === "block-start" ? <section className='bloq-wrap'></section> : ''}
              <MenuEntry key={it.label} item={it} className={['block-start', 'block-end'].includes(it.type) ? 'bloq' : ''} />
              </>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function MenuEntry({ item, className }: { item: Extract<MenuItem, { type: 'entry' }> } & {className?: string }): JSX.Element {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className={`menu-entry${item.disabled ? ' disabled' : ''} ${item.className ? (' ' + item.className) : ''} ${className}`}
      role="menuitem"
      disabled={item.disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (item.disabled) return;
        item.onSelect();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {Icon && <span className="entry-icon"><Icon size={24} /></span>}
      <span style={{ display: 'flex', flexDirection: "column" }}>
        <span className="label">{item.label}</span>
        {item.meta && <span style={{ color: "var(--ink-muted)", fontSize: 10, textTransform: "capitalize" }}>{item.meta}</span>}
      </span>
    </button>
  );
}

/** Re-exported so consumers that don't need the full bar (tests, stubs) can
 *  still see the tick-rate choices. */
export const TICK_RATE_CHOICES = HZ_CHOICES;
